import { Link } from 'react-router-dom'
import { useMemo, useState } from 'react'
import { Calendar, Search, User } from 'lucide-react'
import { Badge } from '@/components/ui'
import { getBlogPageCounts, getBlogPosts } from '@/data/blog'

export function BlogPage() {
  const counts = getBlogPageCounts()
  const posts = getBlogPosts()

  const [query, setQuery] = useState('')
  const q = query.trim().toLowerCase()

  const filtered = useMemo(() => {
    if (!q) return posts
    return posts.filter(
      (p) =>
        p.title.toLowerCase().includes(q) ||
        p.excerpt.toLowerCase().includes(q) ||
        p.slug.includes(q) ||
        p.tags.some((t) => t.toLowerCase().includes(q)),
    )
  }, [posts, q])

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section - immersive style (match BrandsPage) */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage:
              `url('https://images.unsplash.com/photo-1516979187457-637abb4f9353?auto=format&fit=crop&w=1920&q=80')`,
          }}
        />
        <div className="absolute inset-0 bg-black/35" />

        <div className="relative z-10 w-full flex flex-col items-center justify-center px-6">
          <div className="text-center max-w-4xl w-full">
            <h1 className="font-display text-5xl md:text-6xl text-white leading-tight tracking-tight">
              Blog
            </h1>
          </div>

          <div className="mt-10 w-full max-w-2xl">
            <form className="w-full" onSubmit={(e) => e.preventDefault()}>
              <div
                className="backdrop-blur-md rounded-lg px-6 py-4 shadow-xl w-full border border-white/20"
                style={{ backgroundColor: 'rgba(0, 0, 0, 0.21)' }}
              >
                <label
                  htmlFor="blog-search"
                  className="block text-xs uppercase tracking-widest text-white mb-3 text-left font-medium"
                >
                  Search articles
                </label>
                <div className="flex items-center gap-4">
                  <input
                    id="blog-search"
                    type="text"
                    placeholder="Search blog posts..."
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    className="flex-1 text-base text-white placeholder:text-white/70 bg-transparent border-none outline-none focus:outline-none"
                  />
                  <button
                    type="submit"
                    className="text-white hover:text-white/80 transition-colors p-2 hover:bg-white/10 rounded-md"
                  >
                    <Search className="w-6 h-6" />
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </section>

      <section className="py-section-lg px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <p className="text-center text-muted-navy mb-10">
            Client-supplied posts: <span className="font-medium">{counts.posts}</span> (
            <span className="font-medium">{counts.total}</span> routes including the hub).
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filtered.map((p) => (
              <Link
                key={p.slug}
                to={`/blog/${p.slug}`}
                className="group relative bg-white rounded-2xl overflow-hidden border border-line-gray hover:border-navy-900/20 transition-all duration-300 hover:shadow-xl"
              >
                {/* Image Area */}
                <div
                  className={`aspect-[4/3] w-full bg-gradient-to-r ${p.imagePlaceholder} relative overflow-hidden`}
                >
                  <div className="absolute inset-0 bg-gradient-to-t from-navy-900/60 via-navy-900/20 to-transparent" />
                  <div className="absolute top-4 right-4">
                    <Badge
                      variant="secondary"
                      className="bg-white/90 text-navy-900 backdrop-blur-sm"
                    >
                      {p.category}
                    </Badge>
                  </div>
                </div>

                <div className="p-6">
                  <h3 className="font-headline text-2xl font-light text-navy-900 group-hover:text-primary-600 transition-colors line-clamp-2">
                    {p.title}
                  </h3>
                  <p className="mt-4 text-muted-navy text-sm leading-relaxed line-clamp-3">
                    {p.excerpt}
                  </p>

                  <div className="mt-5 flex flex-wrap gap-2">
                    {p.tags.slice(0, 4).map((t) => (
                      <span
                        key={t}
                        className="text-xs px-3 py-1 rounded-full bg-gray-100 text-muted-navy"
                      >
                        {t}
                      </span>
                    ))}
                  </div>

                  <div className="mt-6 flex items-center justify-between text-xs text-muted-navy">
                    <span className="inline-flex items-center gap-1">
                      <User className="h-3.5 w-3.5" />
                      {p.author.name}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <Calendar className="h-3.5 w-3.5" />
                      {p.date}
                    </span>
                  </div>

                  <div className="mt-6 flex items-center text-sm font-medium text-navy-900 group-hover:text-primary-600 transition-colors">
                    <span>Explore</span>
                    <span className="ml-2 group-hover:translate-x-1 transition-transform">→</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {filtered.length === 0 && (
            <div className="mt-10 text-center text-sm text-muted-navy">
              No posts match that search.
            </div>
          )}
        </div>
      </section>
    </div>
  )
}

